import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import ScanBadge from './ScanBadge';
import Button from '../../../components/ui/Button';

const ITEMS_PER_PAGE = 5;

const getJobMatch = (analysis) => {
  // Prefer column if present
  if (typeof analysis?.job_match_score === 'number') return analysis.job_match_score;
  // Fallback to live-analysis JSON
  return analysis?.analysis_results?.analysis?.job_match_score ?? 0;
};

const getImprovementBadge = (improvementScore) => {
  if (!improvementScore && improvementScore !== 0) return null;

  if (improvementScore >= 20) {
    return { label: `+${improvementScore} pts`, emoji: '🎉', color: 'text-green-600', bg: 'bg-green-50' };
  } else if (improvementScore >= 10) {
    return { label: `+${improvementScore} pts`, emoji: '👍', color: 'text-blue-600', bg: 'bg-blue-50' };
  } else if (improvementScore >= 5) {
    return { label: `+${improvementScore} pts`, emoji: '📈', color: 'text-cyan-600', bg: 'bg-cyan-50' };
  } else if (improvementScore >= -5) {
    return { label: `${improvementScore} pts`, emoji: '➖', color: 'text-gray-600', bg: 'bg-gray-50' };
  } else {
    return { label: `${improvementScore} pts`, emoji: '⚠️', color: 'text-red-600', bg: 'bg-red-50' };
  }
};

const RecentAnalysisCard = ({ analyses = [], onViewDetails }) => {
  const navigate = useNavigate();

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-success/10';
    if (score >= 60) return 'bg-warning/10';
    return 'bg-error/10';
  };

  const handleRescan = (analysis) => {
    // Extract the job role from analysis
    const targetRole = analysis.target_role ||
      analysis.analysis_results?.targetRole ||
      analysis.analysis_results?.jobPosting?.title ||
      analysis.analysis_results?.analysis?.job_role;

    const jobPosting = analysis.analysis_results?.jobPosting || {};
    const jobUrl = jobPosting.job_url;
    const jobCompany = jobPosting.company;
    const jobDescription = jobPosting.summary;
    const jobLocation = jobPosting.location;

    // Store in sessionStorage for the upload page to pre-fill
    if (targetRole || jobUrl || jobDescription) {
      sessionStorage.setItem('rescan_data', JSON.stringify({
        targetRole,
        jobUrl,
        jobCompany,
        jobDescription,
        jobLocation,
        previousAnalysisId: analysis.id,
        isRescan: true
      }));
    }

    // Navigate to upload/analysis page
    navigate('/resume-upload-analysis');
  };

  // Calculate scan numbers for each job (excluding FREE tier scans)
  const analysesWithScanNumbers = React.useMemo(() => {
    // CRITICAL: Sort by date ASC (oldest first) to assign correct scan numbers
    const sortedByDate = [...analyses].sort((a, b) =>
      new Date(a.created_at) - new Date(b.created_at)
    );

    const jobCounts = {};
    const numbered = sortedByDate.map(analysis => {
      const isFree = analysis.analysis_results?.paymentStatus?.tier === 'free';

      // Don't number FREE tier scans - they're teasers, not part of scan history
      if (isFree) {
        return { ...analysis, scanNumber: null };
      }

      const jobKey = analysis.target_role ||
        analysis.analysis_results?.jobPosting?.title ||
        'unknown';
      jobCounts[jobKey] = (jobCounts[jobKey] || 0) + 1;
      return { ...analysis, scanNumber: jobCounts[jobKey] };
    });

    // Group by job, then sort groups by most recent scan
    const groupedByJob = {};
    numbered.forEach(analysis => {
      const jobKey = analysis.target_role ||
        analysis.analysis_results?.jobPosting?.title ||
        'unknown';

      if (!groupedByJob[jobKey]) {
        groupedByJob[jobKey] = [];
      }
      groupedByJob[jobKey].push(analysis);
    });

    // Sort job groups by most recent scan, then scans within group by date (newest first)
    return Object.entries(groupedByJob)
      .sort((a, b) => {
        const latestA = Math.max(...a[1].map(s => new Date(s.created_at)));
        const latestB = Math.max(...b[1].map(s => new Date(s.created_at)));
        return latestB - latestA; // Most recently updated job first
      })
      .flatMap(([jobKey, group]) =>
        group.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      );
  }, [analyses]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(analysesWithScanNumbers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentAnalyses = analysesWithScanNumbers.slice(startIndex, endIndex);

  // Reset to page 1 if analyses change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [analyses.length]);


  // Empty state
  if (!analyses || analyses.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-card-foreground">Recent Analyses</h3>
          <Icon name="FileText" size={20} className="text-primary" />
        </div >
        <div className="text-center py-8">
          <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">No analyses yet</p>
          <Button
            variant="default"
            size="sm"
            onClick={() => (window.location.href = '/upload-analysis')}
          >
            Start Your First Analysis
          </Button>
        </div>
      </div >
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base sm:text-lg font-semibold text-card-foreground">Recent Analyses</h3>
        <Icon name="FileText" size={20} className="text-primary" />
      </div>

      <div className="space-y-3">
        {currentAnalyses.map((analysis) => {
          const createdAt = analysis?.created_at
            ? new Date(analysis.created_at).toLocaleDateString('en-IN', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })
            : 'Recent';

          const jm = getJobMatch(analysis);
          const jmBg = jm >= 80 ? 'bg-success/10' : jm >= 60 ? 'bg-warning/10' : 'bg-error/10';
          const jmColor = jm >= 80 ? 'text-success' : jm >= 60 ? 'text-warning' : 'text-error';

          // Get improvement badge data
          const improvementScore = analysis.improvement_score;
          const badge = getImprovementBadge(improvementScore);

          return (
            <div key={analysis.id} className="border border-border rounded-lg p-3 sm:p-4 hover:border-primary/30 transition-colors">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-card-foreground">
                    {(() => {
                      const company = analysis?.analysis_results?.jobPosting?.company;
                      const jobTitle = analysis?.analysis_results?.jobPosting?.title;
                      if (company && jobTitle) return `${company} - ${jobTitle}`;
                      if (company) return company;
                      if (jobTitle) return jobTitle;
                      return analysis?.file_name || 'Resume Analysis';
                    })()}
                  </h4>
                  <ScanBadge scanNumber={analysis.scanNumber} />
                  {/* Free Tier Badge */}
                  {analysis.analysis_results?.paymentStatus?.tier === 'free' && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-warning/20 text-warning border border-warning/30">
                      FREE
                    </span>
                  )}
                  {/* Paid Tier Badge */}
                  {analysis.analysis_results?.paymentStatus?.tier !== 'free' && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-success/20 text-success border border-success/30">
                      PAID
                    </span>
                  )}
                </div>
                {/* Job Match Badge */}
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${jmBg}`}>
                  <span className={jmColor}>Job Match: {jm}%</span>
                </div>
              </div>

              {/* Improvement Badge - Show if this is a re-scan */}
              {badge && (
                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.color} mb-3`}>
                  <span>{badge.emoji}</span>
                  <span>{badge.label}</span>
                  <span className="text-gray-500">since last scan</span>
                </div>
              )}

              <div className="flex items-center justify-between gap-2 pt-3 border-t border-border/50">
                <span className="text-xs text-muted-foreground capitalize">
                  Status: {analysis?.status || 'unknown'}
                </span>
                <div className="flex gap-2">
                  {/* Re-scan Button - Hidden for FREE tier scans */}
                  {analysis.analysis_results?.paymentStatus?.tier !== 'free' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRescan(analysis)}
                      className="text-primary hover:bg-primary/10"
                    >
                      <Icon name="RotateCw" size={14} className="mr-1" />
                      Re-scan
                    </Button>
                  )}
                  {/* View Details Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewDetails(analysis.id)}
                  >
                    View Details
                  </Button>
                </div>
              </div>

              {/* Upgrade Message for Free Tier Scans */}
              {analysis.analysis_results?.paymentStatus?.tier === 'free' && (
                <div className="mt-3 pt-3 border-t border-border/50">
                  <p className="text-xs text-muted-foreground italic">
                    💎 Upgrade to premium for detailed analysis, roadmaps & interview prep
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-border">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded-md text-sm border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-1 rounded-md text-sm border transition-colors ${currentPage === page
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border hover:bg-muted'
                }`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded-md text-sm border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default RecentAnalysisCard;